import { useState } from "react";

import * as service from "../services/creatorStreamsService";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useCreatorStreams = (username) => {
  const [streams, setStreams] = useState([]);
  const [stream, setStream] = useState({});

  const [broadcastData, setBroadcastData] = useState({});
  const [watchData, setWatchData] = useState({});
  const [processingLog, setProcessingLog] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const setTemporaryError = (errorMessage, timeout = 3000) => {
    setError({
      type: "error",
      message: errorMessage,
    });

    setTimeout(() => {
      setError("");
    }, timeout);
  };

  const getStreamsByUsername = async () => {
    if (!username) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getStreamsByUsername(username);
      const data = Array.isArray(res?.data) ? res.data : [];

      setStreams(data);

      return data;
    } catch (error) {
      setStreams([]);

      setTemporaryError(
        getErrorMessage(error, "Failed to fetch creator streams."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const createStream = async (data) => {
    if (!data) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.createStream(data);
      const createdStream = res?.data || {};

      setStream(createdStream);

      await getStreamsByUsername();

      return createdStream;
    } catch (error) {
      setTemporaryError(getErrorMessage(error, "Failed to create stream."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getAllStreams = async (accessType) => {
    try {
      setLoading(true);
      setError("");

      const res = await service.getAllStreams(accessType);
      const data = Array.isArray(res?.data) ? res.data : [];

      setStreams(data);

      return data;
    } catch (error) {
      setStreams([]);

      setTemporaryError(getErrorMessage(error, "Failed to fetch streams."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getStreamById = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getStreamById(streamSessionId);
      const data = res?.data || {};

      setStream(data);

      return data;
    } catch (error) {
      setStream({});

      setTemporaryError(getErrorMessage(error, "Failed to fetch stream."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getStreamSessionById = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getStreamSessionById(streamSessionId);

      const data = res?.data || {};

      setStream(data);

      return data;
    } catch (error) {
      setStream({});

      setTemporaryError(
        getErrorMessage(error, "Failed to fetch stream session."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getStreamWatchData = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getStreamWatchData(streamSessionId);

      const data = res?.data || {};

      setWatchData(data);

      return data;
    } catch (error) {
      setWatchData({});

      setTemporaryError(
        getErrorMessage(error, "Failed to fetch stream watch data."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getStreamBroadcastData = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getStreamBroadcastData(streamSessionId);

      const data = res?.data || {};

      setBroadcastData(data);

      return data;
    } catch (error) {
      setBroadcastData({});

      setTemporaryError(
        getErrorMessage(error, "Failed to fetch stream broadcast data."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const markStreamLive = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.markStreamLive(streamSessionId);
      const updatedStream = res?.data || {};

      setStream(updatedStream);

      setStreams((current) =>
        current.map((item) =>
          String(item.streamSessionId) === String(streamSessionId)
            ? updatedStream
            : item,
        ),
      );

      setBroadcastData((current) => ({
        ...current,
        status: updatedStream.status,
      }));

      setWatchData((current) => ({
        ...current,
        status: updatedStream.status,
      }));

      return updatedStream;
    } catch (error) {
      setTemporaryError(getErrorMessage(error, "Failed to start stream."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const markStreamEnded = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.markStreamEnded(streamSessionId);
      const updatedStream = res?.data || {};

      setStream(updatedStream);

      setStreams((current) =>
        current.map((item) =>
          String(item.streamSessionId) === String(streamSessionId)
            ? updatedStream
            : item,
        ),
      );

      setBroadcastData((current) => ({
        ...current,
        status: updatedStream.status,
      }));

      setWatchData((current) => ({
        ...current,
        status: updatedStream.status,
      }));

      return updatedStream;
    } catch (error) {
      setTemporaryError(getErrorMessage(error, "Failed to end stream."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const processStreamRecording = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.processStreamRecording(streamSessionId);

      const data = res?.data || {};

      setProcessingLog(data);

      await getStreamById(streamSessionId);

      return data;
    } catch (error) {
      setTemporaryError(
        getErrorMessage(error, "Failed to process stream recording."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const linkIvsRecordingToStream = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.linkIvsRecordingToStream(streamSessionId);

      const updatedStream = res?.data?.stream || null;
      const recording = res?.data?.recording || null;

      if (updatedStream) {
        setStream(updatedStream);

        setStreams((current) =>
          current.map((item) =>
            String(item.streamSessionId) === String(streamSessionId)
              ? updatedStream
              : item,
          ),
        );
      }

      return {
        stream: updatedStream,
        recording,
      };
    } catch (error) {
      setTemporaryError(
        getErrorMessage(error, "Failed to link IVS recording."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateStreamDetails = async (streamSessionId, data) => {
    if (!streamSessionId || !data) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.updateStreamDetails(streamSessionId, data);

      const updatedStream = res?.data || {};

      setStream(updatedStream);
      setBroadcastData(updatedStream);

      setStreams((current) =>
        current.map((item) =>
          String(item.streamSessionId) === String(streamSessionId)
            ? updatedStream
            : item,
        ),
      );

      return updatedStream;
    } catch (error) {
      setTemporaryError(
        getErrorMessage(error, "Failed to update stream details."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteStream = async (streamSessionId) => {
    if (!streamSessionId) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.deleteStream(streamSessionId);

      setStreams((current) =>
        current.filter(
          (item) => String(item.streamSessionId) !== String(streamSessionId),
        ),
      );

      if (String(stream?.streamSessionId) === String(streamSessionId)) {
        setStream({});
      }

      return res?.data ?? true;
    } catch (error) {
      setTemporaryError(getErrorMessage(error, "Failed to delete stream."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetCreatorStreams = () => {
    setStreams([]);
    setStream({});
    setBroadcastData({});
    setWatchData({});
    setProcessingLog(null);
    setLoading(false);
    setError("");
  };

  const resetStreamState = () => {
    setStream({});
    setBroadcastData({});
    setWatchData({});
    setProcessingLog(null);
    setError("");
  };

  return {
    streams,
    stream,

    broadcastData,
    watchData,
    processingLog,

    loading,
    error,

    setStreams,
    setStream,
    setBroadcastData,
    setWatchData,
    setProcessingLog,
    setError,

    createStream,
    getAllStreams,
    getStreamsByUsername,

    getStreamById,
    getStreamSessionById,
    getStreamWatchData,
    getStreamBroadcastData,

    markStreamLive,
    markStreamEnded,

    processStreamRecording,
    linkIvsRecordingToStream,

    updateStreamDetails,
    deleteStream,

    resetStreamState,
    resetCreatorStreams,
  };
};

export default useCreatorStreams;
